<script setup>
/**
 * Root Application Component
 * @description Main application wrapper with RouterView and global UI elements
 */

import { watch } from 'vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import Sonner from '@/components/ui/sonner/Sonner.vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationsStore } from '@/stores/notifications'
import { useTheme } from '@/composables/useTheme'
import { toast } from 'vue-sonner'

const authStore = useAuthStore()
const notificationsStore = useNotificationsStore()
const { setTheme } = useTheme()

/**
 * Bridge the notifications Pinia store to vue-sonner so that any code
 * calling notificationsStore.success/error/warning/info gets a real
 * toast rendered in the DOM.
 */
watch(
  () => notificationsStore.notifications.length,
  (newLen, oldLen) => {
    if (newLen <= oldLen) return
    const notification = notificationsStore.notifications[newLen - 1]
    if (!notification) return

    const toastFn = {
      success: toast.success,
      error: toast.error,
      warning: toast.warning,
      info: toast.info,
    }[notification.type] || toast

    toastFn(notification.message, {
      description: notification.title !== notification.type?.charAt(0).toUpperCase() + notification.type?.slice(1) ? notification.title : undefined,
      duration: notification.duration,
    })
  }
)

/**
 * Sync backend preferences to localStorage (and apply theme) once the
 * user profile is available. Keeps the local cache current so that
 * components reading from localStorage see the right values on every
 * page load, even before auth completes on the next visit.
 */
watch(
  () => authStore.user?.preferences,
  (preferences) => {
    if (!preferences) return

    if (preferences.theme) {
      setTheme(preferences.theme)
    }
    if (preferences.notifications) {
      localStorage.setItem('notification_preferences', JSON.stringify(preferences.notifications))
    }
    if (preferences.display) {
      localStorage.setItem('display_preferences', JSON.stringify(preferences.display))
    }
  }
)
</script>

<template>
  <ErrorBoundary>
    <div id="app" class="min-h-screen">
      <!-- Main Router View - No transitions for faster navigation -->
      <RouterView />
    </div>
    <Sonner rich-colors close-button position="bottom-right" />
  </ErrorBoundary>
</template>

<style scoped>
/**
 * Ensure app takes full height
 */
#app {
  display: flex;
  flex-direction: column;
}
</style>
